import { useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import {
  CURRENT_ANALYSIS,
  getCurrentAnalysis,
  getDeviceTimezone,
  getExerciseDetail,
  getExercisePerformance,
  getInsightsAdherence,
  getInsightsMuscleVolume,
  isInsightsTimeframe,
} from "../api/insightsApi";
import useInsightsResource from "../api/useInsightsResource";

// The chosen period lives in the URL (?timeframe=6M), so the Hub and a detail
// screen open on the same period and Back restores it.
export function useTimeframeParam() {
  const [params, setParams] = useSearchParams();
  const raw = params.get("timeframe");
  const timeframe = isInsightsTimeframe(raw) ? raw : CURRENT_ANALYSIS;
  const setTimeframe = useCallback((next) => {
    setParams(next === CURRENT_ANALYSIS ? {} : { timeframe: next }, { replace: true });
  }, [setParams]);
  return [timeframe, setTimeframe];
}

export function insightsPath(path, timeframe) {
  return timeframe === CURRENT_ANALYSIS ? path : `${path}?timeframe=${timeframe}`;
}

// The timezone read here is both sent and part of the cache key, so a cached
// response always matches the request that produced it.
export function useCurrentAnalysis() {
  const timezone = getDeviceTimezone();
  return useInsightsResource(["current-analysis", timezone], () => getCurrentAnalysis(timezone));
}

export function useAdherence(timeframe) {
  const timezone = getDeviceTimezone();
  return useInsightsResource(["adherence", timeframe, timezone], () => getInsightsAdherence(timeframe, timezone));
}

export function useMuscleVolume(timeframe) {
  const timezone = getDeviceTimezone();
  return useInsightsResource(["muscle-volume", timeframe, timezone], () => getInsightsMuscleVolume(timeframe, timezone));
}

// Exercise Performance overview / Hub card: one cache entry per timeframe and
// timezone (and, inside useInsightsResource, per signed-in user).
export function useExercisePerformance(timeframe) {
  const timezone = getDeviceTimezone();
  return useInsightsResource(["exercise-performance", timeframe, timezone], () => getExercisePerformance(timeframe, timezone));
}

// Exercise Detail, keyed by exercise as well: another exercise never shows
// this one's response. Its first sessions page is part of this response.
export function useExerciseDetail(exerciseId, timeframe) {
  const timezone = getDeviceTimezone();
  return useInsightsResource(["exercise-detail", exerciseId, timeframe, timezone], () => getExerciseDetail(exerciseId, timeframe, timezone));
}
